import { Box, Skeleton, Slider, Stack, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import type { JSX } from "react";
import { useState } from "react";

const styles = {
  section: {
    width: "100%",
    p: "0 20px",
  },
  wrapper: {
    width: "100%",
    position: "relative",
    pt: "54px",
    pb: "42px",
  },
  trackLayer: {
    position: "relative",
    height: "24px",
  },
  rail: (theme: Theme): Record<string, unknown> => ({
    position: "absolute",
    left: 0,
    right: 0,
    top: "50%",
    transform: "translateY(-50%)",
    height: "8px",
    borderRadius: "999px",
    backgroundColor: theme.semanticColors.border.subtle,
    zIndex: 0,
  }),
  rangeOverlay: (color: string, left: string, width: string, zIndex: number): Record<string, unknown> => ({
    position: "absolute",
    top: "50%",
    left,
    transform: "translateY(-50%)",
    width,
    height: "8px",
    borderRadius: "999px",
    backgroundColor: color,
    zIndex,
    pointerEvents: "none",
  }),
  sliderBase: {
    width: "100%",
    position: "absolute",
    inset: 0,
  },
  hiddenRail: {
    color: "transparent",
    opacity: 0,
    height: "8px",
  },
  hiddenTrack: {
    color: "transparent",
    border: "none",
    height: "8px",
  },
  hiddenThumb: {
    display: "none",
  },
  editableThumb: (theme: Theme): Record<string, unknown> => ({
    width: 24,
    height: 24,
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.primary.main}`,
    boxShadow: "none",
    display: "flex",
  }),
  valueLabel: (left: string, position: "top" | "bottom", zIndex?: number): Record<string, unknown> => ({
    position: "absolute",
    left,
    transform: "translateX(-50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    whiteSpace: "nowrap",
    backgroundColor: "transparent",
    zIndex: zIndex ?? (position === "top" ? 1 : 2),
    ...(position === "top" ? { top: "18px" } : { bottom: "0" }),
  }),
  topValueText: (theme: Theme): Record<string, unknown> => ({
    color: theme.semanticColors.text.muted,
  }),
  topValueConnector: (theme: Theme): Record<string, unknown> => ({
    width: "1px",
    height: "12px",
    mt: "4px",
    borderLeft: `1px dashed ${theme.semanticColors.text.muted}`,
  }),
  bottomValueBox: (theme: Theme, isMoved: boolean): Record<string, unknown> => ({
    padding: "4px 8px",
    borderRadius: "4px",
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${isMoved ? theme.palette.primary.main : theme.semanticColors.border.subtle}`,
  }),
  bottomValueText: (theme: Theme): Record<string, unknown> => ({
    color: theme.palette.primary.main,
  }),
} as const;

type SliderValueLabelProps = {
  value: number;
  left: string;
  position: "top" | "bottom";
  isEditing?: boolean;
  isMoved?: boolean;
  zIndex?: number;
  dataCy?: string;
  isLoading?: boolean;
};

const SliderValueLabel = ({
  value,
  left,
  position,
  isEditing = false,
  zIndex,
  dataCy,
  isLoading = false,
}: SliderValueLabelProps): JSX.Element => {
  const theme = useTheme();
  const formatCurrency = (nextValue: number): string =>
    nextValue.toLocaleString("de-DE", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    });

  return (
    <Box sx={styles.valueLabel(left, position, zIndex)} data-cy={dataCy}>
      {position === "top" ? (
        <>
          {isLoading ? (
            <Skeleton animation="wave" variant="rounded" width={85} height={20} />
          ) : (
            <Typography sx={styles.topValueText(theme)}>{formatCurrency(value)}</Typography>
          )}
          <Box sx={styles.topValueConnector(theme)} />
        </>
      ) : (
        <Box sx={styles.bottomValueBox(theme, isEditing)}>
          {isLoading ? (
            <Skeleton animation="wave" variant="rounded" width={85} height={20} />
          ) : (
            <Typography sx={styles.bottomValueText(theme)}>{formatCurrency(value)}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

interface PriceRangeSliderProps {
  min: number;
  max: number;
  referenceValue: [number, number];
  initialValue: [number, number];
  value: [number, number];
  isEditing: boolean;
  showBottomLabels: boolean;
  onChange: (_event: Event, value: number | number[], activeThumb: number) => void;
  isLoading?: boolean;
}

export const PriceRangeSlider = ({
  min,
  max,
  referenceValue,
  initialValue,
  value,
  isEditing,
  showBottomLabels,
  onChange,
  isLoading = false,
}: PriceRangeSliderProps): JSX.Element => {
  const theme = useTheme();
  const [activeThumb, setActiveThumb] = useState<number | null>(null);
  const sliderRange = max - min || 1;

  const getThumbPosition = (nextValue: number): string => `${((nextValue - min) / sliderRange) * 100}%`;
  const getRangeWidth = (start: number, end: number): string => `${((end - start) / sliderRange) * 100}%`;

  return (
    <Stack gap={theme.analysisLayout.sectionGap} sx={styles.section}>
      <Box sx={styles.wrapper}>
        <Box sx={styles.trackLayer}>
          <Box sx={styles.rail(theme)} />
          <Box
            sx={styles.rangeOverlay(
              theme.palette.action.disabled,
              getThumbPosition(referenceValue[0]),
              getRangeWidth(referenceValue[0], referenceValue[1]),
              1,
            )}
          />
          <Box
            sx={styles.rangeOverlay(
              theme.palette.primary.main,
              getThumbPosition(value[0]),
              getRangeWidth(value[0], value[1]),
              2,
            )}
          />
          <Slider
            value={referenceValue}
            min={min}
            max={max}
            step={1000}
            disabled
            disableSwap
            sx={{
              ...styles.sliderBase,
              zIndex: 1,
              pointerEvents: "none",
              "& .MuiSlider-rail": styles.hiddenRail,
              "& .MuiSlider-track": styles.hiddenTrack,
              "& .MuiSlider-thumb": styles.hiddenThumb,
              "& .MuiSlider-thumb.Mui-disabled": styles.hiddenThumb,
              "&.Mui-disabled": {
                color: theme.semanticColors.border.actionMuted,
              },
            }}
          />
          <Slider
            value={value}
            min={min}
            max={max}
            step={1000}
            onChange={(event, nextValue, nextActiveThumb) => {
              setActiveThumb(nextActiveThumb);
              onChange(event, nextValue, nextActiveThumb);
            }}
            onChangeCommitted={() => {
              setActiveThumb(null);
            }}
            disabled={isLoading}
            disableSwap
            sx={{
              ...styles.sliderBase,
              zIndex: 3,
              "& .MuiSlider-rail": styles.hiddenRail,
              "& .MuiSlider-track": styles.hiddenTrack,
              "& .MuiSlider-thumb": styles.editableThumb(theme),
              "&.Mui-disabled": {
                color: theme.palette.primary.main,
              },
            }}
          />
        </Box>
        <SliderValueLabel
          value={referenceValue[0]}
          left={getThumbPosition(referenceValue[0])}
          position="top"
          zIndex={1}
          dataCy="price-range-slider-reference-min"
          isLoading={isLoading}
        />
        <SliderValueLabel
          value={referenceValue[1]}
          left={getThumbPosition(referenceValue[1])}
          position="top"
          zIndex={1}
          dataCy="price-range-slider-reference-max"
          isLoading={isLoading}
        />
        {showBottomLabels && (
          <>
            <SliderValueLabel
              value={value[0]}
              left={getThumbPosition(value[0])}
              position="bottom"
              isEditing={isEditing}
              isMoved={value[0] !== initialValue[0]}
              zIndex={activeThumb === 0 ? 4 : 3}
              isLoading={isLoading}
            />
            <SliderValueLabel
              value={value[1]}
              left={getThumbPosition(value[1])}
              position="bottom"
              isEditing={isEditing}
              isMoved={value[1] !== initialValue[1]}
              zIndex={activeThumb === 1 ? 4 : 3}
              isLoading={isLoading}
            />
          </>
        )}
      </Box>
    </Stack>
  );
};
